import React, { useState, useEffect } from 'react';
import { 
  Box, Table, TableBody, TableCell, TableHead, TableRow, 
  Chip, Button, CircularProgress, Alert, Dialog, DialogTitle, 
  DialogContent, DialogActions, Typography, Grid, Divider
} from '@mui/material';
import api from '../../api';

const GeneratePayslips = () => {
  const [activeSheets, setActiveSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, msg: '', type: 'success' });
  
  // Preview Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedSheetId, setSelectedSheetId] = useState(null);

  const fetchActiveSheets = async () => {
    setLoading(true);
    try {
      const res = await api.get('/payroll/payslips/active-datasheets');
      setActiveSheets(res.data || []);
    } catch (err) {
      showNotif('Failed to load active datasheets.', 'error');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchActiveSheets(); }, []);

  const showNotif = (msg, type = 'success') => {
    setNotification({ show: true, msg, type });
    setTimeout(() => setNotification({ show: false, msg: '', type: 'success' }), 5000);
  };

  const handlePreviewClick = async (sheetId) => {
    setSelectedSheetId(sheetId);
    setPreviewLoading(true);
    setDialogOpen(true);
    try {
      const res = await api.get(`/payroll/payslips/preview/${sheetId}`);
      setPreviewData(res.data);
    } catch (err) {
      showNotif('Failed to load preview data.', 'error');
      setDialogOpen(false);
    } finally { setPreviewLoading(false); }
  };

  const handleConfirmGenerate = async () => {
    setPreviewLoading(true);
    try {
      await api.post('/payroll/payslips/generate-batch', { datasheet_id: selectedSheetId });
      showNotif('Payslips generated successfully. Awaiting Admin Approval.');
      setDialogOpen(false);
      fetchActiveSheets(); // Refresh table
    } catch (err) {
      showNotif(err.response?.data?.detail || 'Generation failed.', 'error');
    } finally { setPreviewLoading(false); }
  };

  const renderBankStat = (label, count, total) => {
    const isComplete = count === total;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">{label}:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {count} / {total}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: isComplete ? 'success.main' : 'error.main' }}>
            {isComplete ? '(OK)' : '(MISSING)'}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {notification.show && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.msg}</Alert>}
      <Alert severity="info" sx={{ mb: 3 }}>
        Generating payslips will permanently lock the DataSheet status to "Payslip Locked", preventing any further regenerations for that month.
      </Alert>

      {loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell><b>Payroll Month</b></TableCell>
              <TableCell><b>Generated On</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell align="center"><b>Action</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeSheets.map(s => (
              <TableRow key={s.id}>
                <TableCell>{s.payroll_month} {s.payroll_year}</TableCell>
                <TableCell>{new Date(s.generated_at).toLocaleString()}</TableCell>
                <TableCell><Chip label={s.status} color="success" size="small" /></TableCell>
                <TableCell align="center">
                  <Button variant="contained" size="small" onClick={() => handlePreviewClick(s.id)}>Generate Payslips</Button>
                </TableCell>
              </TableRow>
            ))}
            {activeSheets.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>No Active DataSheets ready for generation.</TableCell></TableRow>}
          </TableBody>
        </Table>
      )}

      {/* PREVIEW & VALIDATION DIALOG - Changed maxWidth to "sm" */}
      <Dialog open={dialogOpen} onClose={() => !previewLoading && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Validate Payslip Generation</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {previewLoading && !previewData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : previewData ? (
            <Grid container spacing={4} sx={{ mt: 1 }}>
              {/* LEFT SIDE: Employee Stats */}
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>Generate for {previewData.month} {previewData.year}</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>Total Employees: <b>{previewData.total_employees}</b></Typography>
                
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Payroll Status:</Typography>
                <Typography variant="body2">Processed: <b>{previewData.payroll_status.processed}</b></Typography>
                <Typography variant="body2" color="error">Hold Salary: <b>{previewData.payroll_status.hold}</b></Typography>
                
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Employee Status:</Typography>
                <Typography variant="body2">Active: <b>{previewData.employee_status.active}</b></Typography>
                <Typography variant="body2" color="warning.main">Notice: <b>{previewData.employee_status.notice}</b></Typography>
              </Grid>

              {/* RIGHT SIDE: Banking Validation */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>Bank Details Validation</Typography>
                <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  {renderBankStat("Bank Name", previewData.banking_stats.bank_name, previewData.total_employees)}
                  {renderBankStat("Account Holder", previewData.banking_stats.account_holder, previewData.total_employees)}
                  {renderBankStat("Account Number", previewData.banking_stats.account_number, previewData.total_employees)}
                  {renderBankStat("Branch Name", previewData.banking_stats.branch_name, previewData.total_employees)}
                  {renderBankStat("IFSC Code", previewData.banking_stats.ifsc_code, previewData.total_employees)}
                  {renderBankStat("PAN Number", previewData.banking_stats.pan_number, previewData.total_employees)}
                  {renderBankStat("PF Number", previewData.banking_stats.pf_number, previewData.total_employees)}
                  {renderBankStat("UAN Number", previewData.banking_stats.uan_number, previewData.total_employees)}
                </Box>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Typography variant="body1" sx={{ flexGrow: 1, fontWeight: 'bold', fontSize: '0.9rem' }}>Continue generating payslips?</Typography>
          <Button onClick={() => setDialogOpen(false)} disabled={previewLoading}>Cancel</Button>
          <Button onClick={handleConfirmGenerate} variant="contained" disabled={previewLoading}>
            {previewLoading ? <CircularProgress size={24} color="inherit" /> : 'Yes, Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GeneratePayslips;